import { useAuth, type Session } from '../store/auth';

/**
 * The one place the browser talks to the API.
 *
 * The access token lives in memory only — never localStorage — so a stolen
 * XSS payload cannot read it and a new tab starts signed out. That means no
 * `target="_blank"` on an authenticated URL: downloads go through here as
 * blobs instead.
 */

export interface Problem {
  code: string;
  detail: string;
  status: number;
  requestId?: string;
  meta?: Record<string, unknown>;
  errors?: { field: string; message: string }[];
}

export class ApiError extends Error {
  readonly problem: Problem;
  constructor(problem: Problem) {
    super(problem.detail);
    this.name = 'ApiError';
    this.problem = problem;
  }
}

/**
 * Access tokens last thirty minutes. Without this a till would sign itself out
 * mid-shift, so a 401 triggers one renewal and the original request is retried
 * once. Parallel 401s share the same renewal rather than starting a stampede.
 */
async function renewSession(): Promise<boolean> {
  const { session, renewal, setRenewal, signIn, signOut } = useAuth.getState();
  if (renewal) return renewal;
  if (!session) return false;

  const attempt = (async (): Promise<boolean> => {
    try {
      const next = await fetch('/v1/auth/refresh', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: session.refreshToken, tenantId: session.user.tenantId }),
      });
      if (!next.ok) {
        signOut();
        return false;
      }
      signIn((await next.json()) as Session);
      return true;
    } catch {
      signOut();
      return false;
    } finally {
      setRenewal(null);
    }
  })();

  setRenewal(attempt);
  return attempt;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  headers: Record<string, string> = {},
  retry = true,
): Promise<T> {
  const token = useAuth.getState().accessToken;
  const res = await fetch(path.startsWith('/v1') ? path : `/v1${path}`, {
    method,
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const text = await res.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  /**
   * A 401 from signing in is not an expired session — it is a wrong password,
   * and the server has already said so in words the person can act on. Treating
   * every 401 the same way replaced "that username or password is not correct"
   * with "your session has ended", which is untrue, unhelpful, and appears
   * before anybody has had a session at all.
   */
  const signingIn = path.includes('/auth/login') || path.includes('/auth/refresh');

  if (res.status === 401 && !signingIn) {
    if (retry && (await renewSession())) {
      return request<T>(method, path, body, headers, false);
    }
    useAuth.getState().signOut();
    throw new ApiError({
      code: 'AUTH_REQUIRED',
      detail: 'Your session has ended. Please sign in again.',
      status: 401,
    });
  }

  if (!res.ok) {
    const problem = (payload ?? {}) as Problem;
    throw new ApiError({
      code: problem.code ?? 'SYS_UNKNOWN',
      detail: problem.detail ?? 'Something went wrong.',
      status: res.status,
      ...(problem.requestId ? { requestId: problem.requestId } : {}),
      ...(problem.meta ? { meta: problem.meta } : {}),
      ...(problem.errors ? { errors: problem.errors } : {}),
    });
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>('GET', path),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T> =>
    request<T>('POST', path, body ?? {}, headers),
  /** Replaces a whole resource. Settings are the only thing shaped that way. */
  put: <T>(path: string, body: unknown): Promise<T> => request<T>('PUT', path, body),
  /** A plain PATCH, for resources that do not use optimistic-locking versions. */
  edit: <T>(path: string, body: unknown): Promise<T> => request<T>('PATCH', path, body),
  /**
   * The version is sent as If-Match so two people editing the same record
   * cannot overwrite each other. A few endpoints keep no version — they change
   * one field and have nothing to race over — and those are called without it.
   */
  patch: <T>(path: string, body: unknown, version?: number): Promise<T> =>
    request<T>('PATCH', path, body, version === undefined ? {} : { 'if-match': String(version) }),
  del: <T>(path: string, reason?: string): Promise<T> =>
    request<T>('DELETE', path, undefined, reason ? { 'x-reason': reason } : {}),

  /** Downloads go through fetch so the in-memory token still applies. */
  download: async (path: string, filename: string): Promise<void> => {
    const token = useAuth.getState().accessToken;
    const res = await fetch(path, { headers: token ? { authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new ApiError({ code: 'EXPORT_FAILED', detail: 'That export could not be produced.', status: res.status });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};
